import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DataTable,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import toast from "react-hot-toast";

import {
  useGetWorkshopsQuery,
  useDeleteWorkshopMutation,
} from "../../store/services/workshops";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";
import { useDebounce } from "../../hooks/useDebounce";
import { WorkshopWithMetrics } from "../../models/workshops";
import {
  setNameFilter,
  setPage,
  setSort,
  setStatus,
  setTenantId,
  selectWorkshopsFilters,
} from "../../store/slices/workshopsFiltersSlice";
import {
  workshopStatusOptions,
  getWorkshopStatusLabel,
  getWorkshopStatusSeverity,
} from "../../utils/constants/workshops-constants";

export const WorkshopListDT = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nameFilter, tenantId, status, first, rows, sortField, sortOrder } =
    useAppSelector(selectWorkshopsFilters);

  const debouncedName = useDebounce(nameFilter, 500);

  const { data: result, isLoading, refetch } = useGetWorkshopsQuery({
    tenantId,
    status,
  });
  const [deleteWorkshop, { isLoading: isDeleting }] = useDeleteWorkshopMutation();
  const { data: tenants } = useGetTenantsQuery({});
  const [workshopToDelete, setWorkshopToDelete] = useState<WorkshopWithMetrics | undefined>();

  const tenantMap = React.useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(tenants)) {
      tenants.forEach((t: any) => map.set(String(t.id), t.provider));
    }
    return map;
  }, [tenants]);

  const tenantOptions = React.useMemo(() => {
    if (!Array.isArray(tenants)) return [];
    return tenants.map((t: any) => ({ label: t.provider, value: String(t.id) }));
  }, [tenants]);

  const filteredData = React.useMemo(() => {
    if (!result?.data) return [];
    if (!debouncedName) return result.data;
    const lower = debouncedName.toLowerCase();
    return result.data.filter(
      (w) =>
        w.name.toLowerCase().includes(lower) ||
        w.activityType.toLowerCase().includes(lower)
    );
  }, [result?.data, debouncedName]);

  const handleDelete = (id: string) => {
    deleteWorkshop(id)
      .unwrap()
      .then(() => {
        toast.success("Taller eliminado exitosamente!");
        refetch();
      })
      .catch((err) => {
        toast.error(
          err?.data?.error?.message || "No se pudo eliminar el taller."
        );
      })
      .finally(() => setWorkshopToDelete(undefined));
  };

  const statusBodyTemplate = (row: WorkshopWithMetrics) => (
    <Tag
      value={getWorkshopStatusLabel(row.status)}
      severity={getWorkshopStatusSeverity(row.status)}
    />
  );

  const capacityBodyTemplate = (row: WorkshopWithMetrics) => (
    <span>
      {row.activeEnrollments}/{row.maxCapacity}
    </span>
  );

  const tenantBodyTemplate = (row: WorkshopWithMetrics) => (
    <span>{tenantMap.get(String(row.tenantId)) || row.tenantId}</span>
  );

  const priceBodyTemplate = (row: WorkshopWithMetrics) => {
    if (!row.isPaid) return <Tag value="Gratuito" severity="success" />;
    return <span>${row.monthlyPrice ? row.monthlyPrice.toLocaleString() : 0}/mes</span>;
  };

  const startDateBodyTemplate = (row: WorkshopWithMetrics) => {
    if (row.isRolling) return <Tag value="Rolling" severity="info" />;
    if (!row.startDate) return "—";
    return new Date(row.startDate * 1000).toLocaleDateString("es-AR");
  };

  const actionsBodyTemplate = (row: WorkshopWithMetrics) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-eye"
        rounded
        text
        severity="info"
        onClick={() => navigate(`/workshops/${row.id}`)}
        tooltip="Ver detalle"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        icon="pi pi-pencil"
        rounded
        text
        severity="success"
        onClick={() => navigate(`/workshops/${row.id}/edit`)}
        tooltip="Editar"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        icon="pi pi-trash"
        rounded
        text
        severity="danger"
        onClick={() => setWorkshopToDelete(row)}
        tooltip="Eliminar"
        tooltipOptions={{ position: "top" }}
      />
    </div>
  );

  const header = (
    <div className="grid">
      <div className="col-12 sm:col-6 lg:col-4 p-inputgroup my-3">
        <span className="p-float-label">
          <InputText
            id="workshopName"
            onChange={(e) => dispatch(setNameFilter(e.currentTarget.value))}
            value={nameFilter}
          />
          <label htmlFor="workshopName">Buscar por nombre</label>
        </span>
        <Button
          icon="pi pi-times"
          className="p-button-danger"
          onClick={() => dispatch(setNameFilter(""))}
        />
      </div>
      <div className="col-12 sm:col-6 lg:col-4 p-inputgroup my-3">
        <Dropdown
          value={tenantId}
          onChange={(e) => dispatch(setTenantId(e.value || undefined))}
          options={tenantOptions}
          optionLabel="label"
          optionValue="value"
          placeholder="Filtrar por tenant"
          showClear
          className="w-full"
        />
      </div>
      <div className="col-12 sm:col-6 lg:col-4 p-inputgroup my-3">
        <Dropdown
          value={status}
          onChange={(e) => dispatch(setStatus(e.value || undefined))}
          options={workshopStatusOptions}
          optionLabel="label"
          optionValue="value"
          placeholder="Filtrar por estado"
          showClear
          className="w-full"
        />
      </div>
    </div>
  );

  return (
    <>
      <DataTable
        value={filteredData}
        loading={isLoading || isDeleting}
        paginator
        first={first}
        rows={rows}
        rowsPerPageOptions={[5, 10, 25, 50]}
        onPage={(event: DataTablePageEvent) =>
          dispatch(setPage({ first: event.first, rows: event.rows }))
        }
        emptyMessage="No se encontraron talleres"
        header={header}
        stripedRows
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(event: DataTableSortEvent) =>
          dispatch(
            setSort({ sortField: event.sortField, sortOrder: event.sortOrder })
          )
        }
        removableSort
        dataKey="id"
      >
        <Column field="name" header="Nombre" sortable style={{ minWidth: "14rem" }} />
        <Column field="activityType" header="Actividad" sortable style={{ minWidth: "10rem" }} />
        <Column header="Tenant" body={tenantBodyTemplate} style={{ minWidth: "10rem" }} />
        <Column header="Inicio" body={startDateBodyTemplate} sortable sortField="startDate" style={{ minWidth: "8rem" }} />
        <Column header="Precio" body={priceBodyTemplate} style={{ minWidth: "8rem" }} />
        <Column header="Inscriptos" body={capacityBodyTemplate} style={{ minWidth: "8rem" }} />
        <Column field="status" header="Estado" body={statusBodyTemplate} sortable style={{ minWidth: "8rem" }} />
        <Column header="Acciones" body={actionsBodyTemplate} style={{ minWidth: "12rem" }} />
      </DataTable>

      <Dialog
        visible={!!workshopToDelete}
        style={{ width: "450px" }}
        header="Confirmar eliminación"
        modal
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              outlined
              onClick={() => setWorkshopToDelete(undefined)}
            />
            <Button
              label="Eliminar"
              icon="pi pi-trash"
              severity="danger"
              onClick={() => workshopToDelete && handleDelete(workshopToDelete.id)}
              loading={isDeleting}
            />
          </div>
        }
        onHide={() => setWorkshopToDelete(undefined)}
      >
        <div className="flex align-items-center gap-3">
          <i className="pi pi-exclamation-triangle" style={{ fontSize: "2rem" }}></i>
          <span>
            ¿Está seguro que desea eliminar el taller{" "}
            <strong>{workshopToDelete?.name}</strong>?
          </span>
        </div>
      </Dialog>
    </>
  );
};
